import {Checkbox} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {InputChangeEvent} from 'util/Types';

import {StudySearchItem} from './StudySearchInput';

export interface StudyAutoLinkingInputProps {
  selectedStudies: Array<StudySearchItem>;
  invalidItemNames: Array<string>;
  isAutoLinkingChecked: boolean;
  isAutoLinkingDisabled: boolean;
  onAutoLinkingCheckedChange: (e: InputChangeEvent) => void;
  onAutoLinkingStudyChange: (studyId: string) => void;
  // The study that the item will be linked to
  autoLinkingStudy?: {
    id: string;
    name: string;
  };
  enableAutoLinkingSelection: boolean;
  autoLinkingExtensionsFlagEnabled: boolean;
}

export default function StudyAutoLinkingInput({
  selectedStudies,
  invalidItemNames,
  isAutoLinkingChecked,
  isAutoLinkingDisabled,
  onAutoLinkingCheckedChange,
  onAutoLinkingStudyChange,
  autoLinkingStudy,
  enableAutoLinkingSelection,
  autoLinkingExtensionsFlagEnabled,
}: StudyAutoLinkingInputProps): JSX.Element {
  const isMultipleSelectedItems = selectedStudies.length > 1;
  const filteredStudies = selectedStudies.filter((study) => !invalidItemNames.includes(study.name));
  return (
    <>
      {autoLinkingStudy && !enableAutoLinkingSelection && (
        <div className={css(styles.autoLinkingCheckbox)}>
          <CheckboxInput
            id="study-auto-link-checkbox"
            label={
              <div>
                Automatically link items created in this entry to <strong>{autoLinkingStudy.name}</strong>
              </div>
            }
            disabled={isAutoLinkingDisabled}
            checked={isAutoLinkingChecked}
            onChange={onAutoLinkingCheckedChange}
          />
        </div>
      )}
      {autoLinkingExtensionsFlagEnabled && autoLinkingStudy && enableAutoLinkingSelection && (
        <div className={css(styles.autoLinkingSection)}>
          <>
            <Checkbox
              className={css(styles.autoLinkingCheckbox)}
              id="study-auto-link-checkbox"
              checked={isAutoLinkingChecked}
              onChange={onAutoLinkingCheckedChange}
              disabled={isAutoLinkingDisabled}
              label={
                <>
                  Automatically link items created in this entry to{' '}
                  {!isMultipleSelectedItems && <strong>{autoLinkingStudy.name}</strong>}
                </>
              }
            />
            {isMultipleSelectedItems && (
              <span className={css(styles.autoLinkingDropdown)}>
                <SelectDropdown
                  ariaLabel="Auto-linking study dropdown"
                  displayName={autoLinkingStudy.name}
                  menu={
                    <Menu value={autoLinkingStudy.id} width={Menu.WIDTHS.FULL}>
                      {filteredStudies.map((study) => (
                        <Option
                          key={study.id}
                          value={study.id}
                          onSelect={() => {
                            onAutoLinkingStudyChange(study.id);
                          }}
                        >
                          {study.name}
                        </Option>
                      ))}
                    </Menu>
                  }
                />
              </span>
            )}
          </>
        </div>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  autoLinkingSection: {
    alignItems: 'flex-start',
    display: 'flex',
    marginTop: 5,
  },
  autoLinkingCheckbox: {
    marginTop: 5,
  },
  autoLinkingDropdown: {
    flex: 1,
    marginLeft: 4,
    minWidth: 0,
  },
});
